import { useEffect, useMemo, useState } from 'react'
import { CreditCard, TrendingUp, Edit3, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/lib/hooks/use-toast'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { formatCurrency } from '../../utils'
import { useFetchResumeByIdApuracao } from '../../services'
import { useUpdateResumes } from '../../services/useUpdateResumes'
import { useCurrentUser } from '@/features/auth/state/auth'

type ResumoFCVSProps = {
  id_apuracao?: string
}

export default function ResumoFCVS({ id_apuracao }: ResumoFCVSProps) {
  const { data: resumeData } = useFetchResumeByIdApuracao(id_apuracao)

  const { saldo, id_saldo, credit } = useMemo(() => {
    const restItem = resumeData?.find((r: { typeValue: string }) => r.typeValue === 'SALDO')
    const creditItem = resumeData?.find((r: { typeValue: string }) => r.typeValue === 'CREDITO')
    return {
      saldo: restItem?.value ?? 0,
      id_saldo: restItem?.id ?? '',
      credit: creditItem?.value ?? 0
    }
  }, [resumeData])

  const user = useCurrentUser()
  const canEdit = user?.typeUser === 'ADMIN'

  // Estado numérico consolidado
  const [restValue, setRestValue] = useState<number>(saldo)

  // Estado temporário de string para edição
  const [tempRestValue, setTempRestValue] = useState<string>(
    saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  )

  // Sincroniza quando resumeData mudar
  useEffect(() => {
    setRestValue(saldo)
    setTempRestValue(saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
  }, [saldo])

  // Flags de edição
  const [isEditingRest, setIsEditingRest] = useState<boolean>(false)

  const useUpdateResume = useUpdateResumes()

  const handleSaveRest = async () => {
    if (!canEdit) return
    const parsed = Number(tempRestValue.replace(/\./g, '').replace(',', '.'))
    if (isNaN(parsed)) {
      toast({ title: 'Valor inválido', description: 'Por favor, insira um valor válido.', variant: 'destructive' })
      return
    }
    setRestValue(parsed)
    setIsEditingRest(false)

    await handleUpdateValue(id_saldo, parsed)

    toast({ title: 'Valor atualizado', description: `Valor restituído atualizado para R$ ${formatCurrency(parsed)}` })
  }

  const handleCancelRest = () => {
    setTempRestValue(restValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    setIsEditingRest(false)
  }

  const handleUpdateValue = async (id: string, valor: number) => {
    if (!canEdit) return
    if (!id) {
      console.error('ID inexistente para atualização de resumo.')
      return
    }
    try {
      await useUpdateResume.mutateAsync({ id, valor })
    } catch (error) {
      console.error('Erro ao atualizar valor:', error)
      toast({
        title: 'Erro ao atualizar',
        description: 'Não foi possível salvar o valor. Tente novamente.',
        variant: 'destructive'
      })
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
      {/* Crédito Apurado (somente leitura para todos) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-green-700">Crédito Apurado</h3>
          <TrendingUp className="w-5 h-5 text-green-600" />
        </div>
        <p className="text-3xl font-bold text-green-600 mb-1">{formatCurrency(credit)}</p>
        <p className="text-sm text-gray-600">Total de créditos calculados</p>
      </div>

      {/* Valor Restituído */}
      <div
        className={[
          'bg-white rounded-xl shadow-sm border-2 p-6 transition-all duration-200 relative',
          canEdit ? 'cursor-pointer' : 'cursor-default',
          isEditingRest ? 'border-blue-300 shadow-lg' : 'border-blue-200 hover:border-blue-300 hover:shadow-md'
        ].join(' ')}
        onClick={() => canEdit && !isEditingRest && setIsEditingRest(true)}
        aria-disabled={!canEdit}
      >
        {!isEditingRest && canEdit && (
          <div className="absolute top-2 right-2 bg-blue-100 rounded-full p-1">
            <Edit3 className="w-3 h-3 text-blue-600" />
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-blue-700">Saldo</h3>
          <CreditCard className="w-5 h-5 text-blue-600" />
        </div>

        {isEditingRest && canEdit ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <MaskInputDin
                value={parseFloat(tempRestValue.replace(/\./g, '').replace(',', '.')) || 0}
                onChange={v =>
                  setTempRestValue(v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                }
                className="text-2xl font-bold text-blue-600 border-blue-200 focus:border-blue-400"
                placeholder="0,00"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSaveRest} className="bg-green-600 hover:bg-green-700 text-white">
                <Check className="w-3 h-3 mr-1" />
                Salvar
              </Button>
              <Button size="sm" variant="outline" onClick={handleCancelRest} className="border-gray-300">
                <X className="w-3 h-3 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-3xl font-bold text-blue-600 mb-1">{formatCurrency(restValue)}</p>
            <div className="flex items-center gap-2">
              <p className="text-sm text-gray-600">Valor já restituído</p>
              {canEdit && (
                <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                  <Edit3 className="w-3 h-3" />
                  Clique para editar
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
